import { FC, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchKartGhost, useKartRecords, useKartWeek, useSaveKartRun } from "../api/kart-api";
import { useLogeMembers } from "../api/user-api";
import { GAME_ROUTES, GameKey } from "../components/dss/GameTabs";
import KartRace from "../components/dss/kart/KartRace";
import { ROSTER, ROSTER_KEYS, characterOf } from "../components/dss/kart/models";
import { MAPS, trackId, variantOf, weeklyChallenge } from "../components/dss/kart/tracks";
import { Skeleton } from "../components/dss/Skeleton";
import { useSupabaseAuth } from "../supabase/SupabaseContext";

const KartPage: FC = () => {
  const { data: members, isLoading } = useLogeMembers();
  const { member } = useSupabaseAuth();
  const navigate = useNavigate();
  const from = (useLocation().state as { from?: GameKey } | null)?.from;
  // Back to the game we came from, or the coin if we came straight here.
  const exit = () => navigate(GAME_ROUTES[from && from !== "kart" ? from : "lod"], { replace: true, state: { from: "kart" } });

  // You pick a character from the roster (your own, if you're one of the
  // seven); the other six are the field.
  const me = useMemo(
    () => member && { id: member.id, name: member.name, emoji: member.emoji || "🙂" },
    [member],
  );
  // Every race saved, and the lodge's bests read back — named, for the results.
  const saveRun = useSaveKartRun();
  const { data: recordRows } = useKartRecords();
  const records = useMemo(
    () =>
      (recordRows ?? []).flatMap((b) => {
        const m = members?.find((x) => x.id === b.memberId);
        return m ? [{ ...b, name: m.name, emoji: m.emoji || "🙂" }] : [];
      }),
    [recordRows, members],
  );
  // A member by id, for naming a leaderboard's rows.
  const named = useCallback(
    (id: number) => {
      const m = members?.find((x) => x.id === id);
      return m && { name: m.name, emoji: m.emoji || "🙂" };
    },
    [members],
  );
  // The lodge's fastest run on a track, as a ghost to race.
  const loadGhost = useCallback(
    async (track: string) => {
      const ghost = await fetchKartGhost(track);
      const who = ghost && named(ghost.memberId);
      return ghost && who ? { time: ghost.time, path: ghost.path, ...who } : null;
    },
    [named],
  );
  // This week's challenge, and everyone's best at it so far.
  const challenge = useMemo(() => weeklyChallenge(), []);
  const { data: weekRows } = useKartWeek(
    trackId(variantOf(MAPS.find((m) => m.key === challenge.mapKey)!, challenge.variant)),
    challenge.difficulty,
    challenge.since,
    challenge.goal,
  );
  const week = useMemo(
    () => (weekRows ?? []).flatMap((r) => {
      const who = named(r.memberId);
      return who ? [{ ...r, ...who }] : [];
    }),
    [weekRows, named],
  );
  // The roster, each character under the id of the member they are, where
  // there is one — so a race against Holst is a race against Holst's records.
  const roster = useMemo(
    () =>
      ROSTER_KEYS.map((char, i) => ({
        id: members?.find((m) => characterOf({ name: m.name }) === char)?.id ?? -(i + 1),
        name: ROSTER[char].name,
        emoji: ROSTER[char].emoji,
        char,
      })),
    [members],
  );

  return (
    // The whole screen, over the app's header and tab bar, clear of the
    // notch and the home bar; the ✕ in the corner goes back.
    <div
      className="fixed inset-0 z-40 bg-ink"
      style={{
        padding:
          "env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)",
      }}
    >
      {isLoading || !me ? (
        <Skeleton className="w-full h-full" />
      ) : (
        <KartRace
          me={me}
          opponents={roster}
          onExit={exit}
          records={records}
          loadGhost={loadGhost}
          week={week}
          onRun={(run) => saveRun.mutate({ ...run, memberId: me.id }, { onError: (e) => console.error("Kart run not saved", e) })}
        />
      )}
    </div>
  );
};

export default KartPage;
